import cn from "classnames";
import * as Eden from "src/eden/content";
import * as Team from "src/state/modules/team";
import { useGetAllLiveGamesQuery } from "src/state/modules/api";
import { formatLiveMeta } from "src/utils/map-events";
import LivePlayer from "src/components/compounds/LivePlayer/LivePlayer";
import { Avatar } from "src/components/atoms/Avatar/Avatar";
import { Text } from "src/components/atoms/Text/Text";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import baseballBg from "src/images/live-game-bg-baseball.jpg";
import softballBg from "src/images/live-game-bg-softball.jpg";
import Styles from "./LiveGamesPage.module.scss";

// Refresh the whole feed on the same cadence as the team home page's live section: often enough
// to catch scores and newly-live games, throttled off for background tabs. The server caches the
// aggregation, so this poll is cheap regardless of how many viewers have the page open.
const LIVE_POLL_MS = 30000;

const sportBackground = (sport: string): string => (sport === Team.Sport.SOFTBALL ? softballBg : baseballBg);

// One live game: the click-to-play stream when it's broadcasting, otherwise a "live, no video"
// placeholder so a scored-but-unstreamed game still shows the score instead of vanishing.
const LiveGameCard = ({ item }: { item: Eden.LiveTeamGame }) => {
  const { team_id, team_name, sport, game, video } = item;
  const isPlayable = !!video?.is_playable;
  const meta = formatLiveMeta(game);

  return (
    <div className={Styles.card} data-testid="live-game-card">
      <div className={Styles.cardHeader}>
        <Avatar.Team className={Styles.avatar} sportIcon={(sport as Team.Sport) ?? Team.Sport.BASEBALL} size="small" />
        <div className={Styles.cardHeaderText}>
          <Text weight="semibold" className={Styles.teamName}>
            {team_name}
          </Text>
          {meta && (
            <Text size="small" color="cool-grey-dark" weight="semibold" className={Styles.meta}>
              {meta}
            </Text>
          )}
        </div>
        <span className={Styles.liveBadge}>● LIVE</span>
      </div>

      {isPlayable && video ? (
        <>
          <div className={Styles.mediaBox} data-testid="live-game-player">
            <LivePlayer teamId={team_id} eventId={game.event_id} poster={video.thumbnail_url} />
          </div>
          <Text size="small" color="cool-grey-dark" weight="semibold" className={Styles.viewers}>
            {`${video.viewer_count} watching`}
          </Text>
        </>
      ) : (
        <div
          className={cn(Styles.mediaBox, Styles.placeholder)}
          style={{ backgroundImage: `url(${sportBackground(sport)})` }}
          data-testid="live-game-no-video"
        >
          <div className={Styles.placeholderScrim} />
          <div className={Styles.placeholderContent}>
            <span className={cn(Styles.liveBadge, Styles.placeholderBadge)}>● LIVE</span>
            <Text block weight="xbold" color="white" className={Styles.placeholderHeadline}>
              Live now
            </Text>
            <Text block weight="semibold" color="white" className={Styles.placeholderSubhead}>
              Video isn't available for this game
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

const LiveGamesPage = () => {
  const { data, isLoading } = useGetAllLiveGamesQuery(undefined, {
    pollingInterval: LIVE_POLL_MS,
    skipPollingIfUnfocused: true,
  });

  const games = data ?? [];

  return (
    <div className={Styles.page}>
      <div className={Styles.header}>
        <Text block size="xlarge" weight="xbold" className={Styles.title}>
          Live Games
        </Text>
        {games.length > 0 && <span className={Styles.count}>{`${games.length} live`}</span>}
      </div>

      {isLoading ? (
        <PageLoader1 />
      ) : games.length === 0 ? (
        <div className={Styles.empty} data-testid="live-empty">
          <Text block weight="xbold" size="large">
            No games are live right now
          </Text>
          <Text block color="cool-grey-dark">
            Live games show up here automatically while teams are playing.
          </Text>
        </div>
      ) : (
        <div className={Styles.grid}>
          {games.map((item) => (
            <LiveGameCard key={`${item.team_id}-${item.game.event_id}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveGamesPage;
